import { useCallback, useState } from 'react'

import { getAllSectionGroups } from '../../../../utils/api/sectionGroup'
import { TableParameters } from '../../../../utils/entities/tableParameters'
import { Order } from '../../../../utils/entities/queries'
import { OrderDirections } from '../../../../utils/constants'
import useSectionGroups from '../../../../utils/api/hooks/useSectionGroups'
import { UpdateModes } from '../../../../utils/enums/enums'
import { SectionGroup } from '../../../../utils/entities/sectionGroup'
import SectionGroupsTable from './SectionGroupsTable'
import useQuery from '../../../../hooks/useQuery'
import SectionGroupModalProvider from '../../../../Providers/Forms/SectionGroupForm/SectionGroupModal'
import { resolvePromises } from '../../../../utils/apiHelper'
import { Metadata } from '../../../../utils/http'

export const TableActions = Object.freeze({
  DELETE: 'T_DELETE'
})

const UPDATE_MODE = UpdateModes.SYNCHRONOUS

const DEFAULT_PARAMS = {
  page: 1,
  pageSize: 20,
  order: new Order({ by: 'id', direction: OrderDirections.ASC }),
  search: '',
  filters: []
}

const SectionGroupsView = () => {
  const [tableParameters, setTableParameters] = useState(new TableParameters({ ...DEFAULT_PARAMS }))

  const sectionGroups = useQuery({
    request: getAllSectionGroups,
    defaultParameters: new TableParameters(tableParameters).toQueryParameters(),
    defaultMetadata: new Metadata({ ...tableParameters }),
    select: groups => groups.map(g => new SectionGroup(g))
  })

  const handler = useSectionGroups({ mode: UPDATE_MODE, callback: sectionGroups.refetch })

  const handlePaginationChange = useCallback(targetPage => {
    const { first, last, prev, next } = sectionGroups.metadata

    switch (targetPage) {
      case first: sectionGroups.first()
        break
      case last: sectionGroups.last()
        break
      case next: sectionGroups.next()
        break
      case prev: sectionGroups.previous()
        break
      default:
        break
    }
  }, [sectionGroups.metadata])

  const handleTableParametersChange = useCallback(tableParameters => {
    const queryParameters = new TableParameters({
      ...sectionGroups.parameters,
      ...tableParameters
    })

    setTableParameters(queryParameters)

    const { first, page } = sectionGroups.metadata

    if (queryParameters.page !== page && queryParameters.page !== first) {
      handlePaginationChange(queryParameters.page)
    } else {
      sectionGroups.setParameters(queryParameters.toQueryParameters())
    }
  }, [sectionGroups.parameters, sectionGroups.metadata, handlePaginationChange])

  const handleAction = useCallback((...itemActionPairs) => {
    const promises = []

    for (let iPair = 0; iPair < itemActionPairs.length; iPair += 2) {
      const item = itemActionPairs[iPair]
      const action = itemActionPairs[iPair + 1]

      if (action === TableActions.DELETE) {
        promises.push(handler.delete(item))
      }
    }

    resolvePromises(promises).then(sectionGroups.refetch)
  }, [handler.delete, sectionGroups.refetch])

  return (
    <SectionGroupModalProvider mode={UPDATE_MODE} throwErrors>
      <SectionGroupsTable
        data={sectionGroups.data}
        metadata={sectionGroups.metadata}
        loading={sectionGroups.loading}
        tableParameters={tableParameters}
        onAction={handleAction}
        onTableParametersChange={handleTableParametersChange}
      />
    </SectionGroupModalProvider>
  )
}

export default SectionGroupsView
